import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Box,
    Drawer,
    IconButton,
    Typography,
    TextField,
    Button,
    Paper,
    Divider,
    CircularProgress,
    Chip,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Autocomplete,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Tabs,
    Tab,
    TablePagination
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
    PositionModel,
    UpdatePositionModel,
    UpdateDealTicketRequest,
    CommodityModel,
    CounterpartyModel,
    LocationModel,
    PortfolioModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    ContractDate,
    Side,
    PositionType,
    Unit,
    Currency,
    OptionType,
    OptionStyle,
    BudgetPricingStrategyModel
} from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { useNavigate } from 'react-router';
import { parseMultiTabTradeFile } from './MultiTabTradeFileParser';
import {
    SavedFileMapping,
    findSavedMappingForFile,
    recordSavedMapping,
    loadSavedMappings,
    deleteSavedMapping,
    clearAllSavedMappings,
    getSavedTarget
} from './FileMappingStorage';
import { parseChatQuery } from './ChatQueryParser';
import { describeFiscalYearRange } from './ChatFiscalYear';
import { aggregateRows, describeAggregate, type AggregateSpec } from './ChatAggregates';
import { useChatResults, type ChatResultPayload } from './ChatResultsContext';
import { useChatUi } from './ChatUiContext';
import ImportWizard from '../Import/ImportWizard';
 import EfpContractChatPanel from '../Efp/EfpContractChatPanel';
 import { isEfpContractFile } from '../Efp/useEfpContract';
import type { ImportContext } from '../Import/ImportRow';
import { PERSISTED_FIELD_SEPARATOR, getColumnUnion } from '../Import/ColumnMapping';
import { findDuplicatePosition, monthToNumber, type DuplicateCheckPosition } from '../../../../Helpers';

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    file?: File;
    // Optional rich result rendered below the message text - used by the
    // query branch so we can embed the existing positions/budgets/
    // allocations tables directly inside the chat conversation.
    resultElement?: React.ReactNode;
    // Optional structured payload describing the same result. When
    // present, a "View in full page" button is rendered so the user
    // can open the table on a dedicated route with the wide main
    // content area instead of the cramped chat column.
    resultPayload?: ChatResultPayload;
}

interface ParsedPositionRow {
    rowNumber: number;
    id: string;
    sheetName?: string;
    data: {
        commodityName?: string;
        counterpartyName?: string;
        locationName?: string;
        portfolioName?: string;
        tradeDate?: string;
        dealTicketNumber?: string;
        volume: number;
        price: number;
        unit: Unit;
        currency: Currency;
        side: Side;
        positionType: PositionType;
        budgetDate: ContractDate;
        contractDate: ContractDate;
        optionStrike?: number;
        optionType?: OptionType;
        optionStyle?: OptionStyle;
    };
    mappedData: {
        commodity?: CommodityModel;
        counterparty?: CounterpartyModel;
        location?: LocationModel;
        portfolio?: PortfolioModel;
    };
    unmappedData: Record<string, any>;
    errors: { field: string; message: string }[];
    warnings: { field: string; message: string }[];
}

interface UnmappedColumn {
    originalName: string;
    sampleValues: string[];
    mappedTo?: string;
}

interface ColumnMapping {
    [originalColumn: string]: string | null; // null means ignore, string is the target field
}

interface ChatAssistantProps {
    /** Optional hook so a host can refresh its own data after a successful upload. */
    onDataRefresh?: () => void;
}

// Known field mappings. Each list contains likely column-header variants that
// should auto-resolve to the given PositionModel field. Lazy matching (camelCase
// splitting, separator/case normalization, token-set/subset, and basic plural
// stripping) is applied on top, so e.g. "Trade_Date", "tradeDate", and
// "TRADE-DATE *" all resolve to 'tradeDate' without needing explicit aliases.
const KNOWN_FIELD_MAPPINGS: Record<string, string[]> = {
    'commodity': [
        'Commodity', 'commodity', 'Commodity Name', 'Commodity*',
        'Product', 'Product Name', 'product'
    ],
    'counterparty': [
        'Counterparty', 'counterparty', 'Counterparty Name', 'Counterparty*',
        'Vendor', 'vendor', 'Supplier', 'supplier',
        'Customer', 'Trading Partner', 'Trade Partner', 'Broker'
    ],
    'location': [
        'Location', 'location', 'Location Name', 'Location*',
        'Plant', 'Plant Code', 'plant', 'Site', 'Site Name', 'site',
        'Origin', 'Destination', 'Elevator', 'Facility'
    ],
    'portfolio': [
        'Portfolio', 'portfolio', 'Portfolio Name', 'Portfolio*',
        'Account', 'account', 'Strategy', 'Book', 'Trading Book'
    ],
    'tradeDate': [
        'Trade Date', 'TradeDate', 'tradeDate', 'Trade Date*',
        'Date', 'date', 'Booked Date', 'Entry Date', 'Trade Day', 'Transaction Date'
    ],
    'dealTicketNumber': [
        'Deal Ticket', 'DealTicket', 'Deal Ticket Number', 'dealTicketNumber',
        'Deal Ticket*', 'Deal Number', 'Deal ID', 'Deal Id',
        'Ticket', 'Ticket Number', 'Ticket No', 'Ticket #',
        'Trade Number', 'Trade ID', 'Trade Id', 'Confirmation Number',
        'PO', 'PO Number', 'Reference', 'Reference Number'
    ],
    'volume': [
        'Volume', 'volume', 'Volume*', 'volumes',
        'Quantity', 'quantity', 'Quantities',
        'Qty', 'qty',
        'Quantity Bushels', 'Quantity Contracts', 'Quantity Tons',
        'Volume Bushels', 'Volume Contracts', 'Volume Tons',
        'Bushels', 'Tons', 'Tonnes', 'Lots', 'Contracts', 'Number of Contracts'
    ],
    'price': [
        'Price', 'price', 'Price*',
        'Rate', 'rate', 'Cost', 'cost',
        'Trade Price', 'Unit Price', 'Price Per Unit', 'Fixed Price', 'Basis Price', 'Net Price'
    ],
    'unit': [
        'Unit', 'unit', 'Unit*', 'Units',
        'UOM', 'uom', 'Unit Of Measure', 'Measure', 'Measurement'
    ],
    'currency': [
        'Currency', 'currency', 'Currency*',
        'Ccy', 'ccy', 'CCY', 'Curr', 'Currency Code'
    ],
    'side': [
        'Side', 'side', 'Side*',
        'Buy/Sell', 'Buy Sell', 'Buy or Sell', 'B/S', 'Direction', 'direction',
        'Trade Side', 'Position Side', 'Action'
    ],
    'positionType': [
        'Position Type', 'PositionType', 'positionType', 'Position Type*',
        'Type', 'type', 'Instrument', 'Instrument Type',
        'Trade Type', 'Product Type', 'Contract Type'
    ],
    'budgetMonth': [
        'Budget Month', 'BudgetMonth', 'budgetMonth', 'Budget Month*',
        'Delivery Month',
        'Budget Date', 'BudgetDate', 'Delivery Date'
    ],
    'budgetYear': [
        'Budget Year', 'BudgetYear', 'budgetYear', 'Budget Year*',
        'Delivery Year'
    ],
    'contractMonth': [
        'Contract Month', 'ContractMonth', 'contractMonth', 'Contract Month*',
        'Futures Month', 'Maturity Month',
        'Contract Date', 'ContractDate'
    ],
    'contractYear': [
        'Contract Year', 'ContractYear', 'contractYear', 'Contract Year*',
        'Futures Year', 'Maturity Year'
    ],
    'optionStrike': [
        'Option Strike', 'OptionStrike', 'optionStrike', 'Option Strike*',
        'Strike', 'strike', 'Strike Price', 'StrikePrice', 'Strike*'
    ],
    'optionType': [
        'Option Type', 'OptionType', 'optionType', 'Option Type*',
        'Call/Put', 'Call Put', 'Put/Call', 'Put Call', 'C/P', 'P/C'
    ],
    'optionStyle': [
        'Option Style', 'OptionStyle', 'optionStyle', 'Option Style*',
        'Style', 'style', 'Exercise Style', 'ExerciseStyle'
    ],
};

// Available target fields for mapping
const AVAILABLE_TARGET_FIELDS = [
    { value: 'commodity', label: 'Commodity' },
    { value: 'counterparty', label: 'Counterparty' },
    { value: 'location', label: 'Location' },
    { value: 'portfolio', label: 'Portfolio' },
    { value: 'tradeDate', label: 'Trade Date' },
    { value: 'dealTicketNumber', label: 'Deal Ticket Number' },
    { value: 'volume', label: 'Volume' },
    { value: 'price', label: 'Price' },
    { value: 'unit', label: 'Unit' },
    { value: 'currency', label: 'Currency' },
    { value: 'side', label: 'Side (Buy/Sell)' },
    { value: 'positionType', label: 'Position Type' },
    { value: 'budgetMonth', label: 'Budget Month' },
    { value: 'budgetYear', label: 'Budget Year' },
    { value: 'contractMonth', label: 'Contract Month' },
    { value: 'contractYear', label: 'Contract Year' },
    { value: 'optionStrike', label: 'Option Strike' },
    { value: 'optionType', label: 'Option Type' },
    { value: 'optionStyle', label: 'Option Style' },
    { value: 'ignore', label: '-- Ignore this column --' },
];

// Renders the assistant body either in the right-hand drawer or, when the user has
// switched to full page, portalled into the container AssistantPage registered with
// ChatUiContext. Keeping this as a wrapper (rather than mounting ChatAssistant twice)
// means the conversation and any staged import survive the switch.
// The assistant's colours are the site's, taken from the CSS variables the header,
// sidebar and buttons already use (Styles/index.css). MUI's default `primary` blue is
// not set anywhere in the site theme, so anything painted with it stood out.
const CHAT_COLORS = {
    /** Same midnight as the sidebar. */
    headerBackground: 'var(--navigation-background-color)',
    headerText: 'var(--white)',
    /** The site's button blue: the user's bubbles, the send icon, the assistant icon. */
    accent: 'var(--button-primary-color)',
    assistantBubble: 'var(--gray8)',
    /** Side panes and empty areas. */
    panel: 'var(--gray9)',
    /** Rows and banners that need to stand apart from white. */
    highlight: 'var(--gray7)',
} as const;

function ChatShell(props: {
    isFullPage: boolean;
    fullPageSlot: HTMLElement | null;
    open: boolean;
    onClose: () => void;
    wide: boolean;
    children: React.ReactNode;
}) {
    const { isFullPage, fullPageSlot, open, onClose, wide, children } = props;

    const body = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {children}
        </Box>
    );

    if (isFullPage) {
        // The route is mounted but its container has not attached yet - render nothing
        // this tick rather than falling back to the drawer, which would flash it open.
        return fullPageSlot ? createPortal(body, fullPageSlot) : null;
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: wide ? { xs: '100%', md: '80%', lg: '70%' } : { xs: '100%', sm: 500, md: 600 },
                    maxWidth: '100%',
                    transition: 'width 0.3s ease'
                }
            }}
        >
            {body}
        </Drawer>
    );
}

// Intent detection for user messages
function detectIntent(message: string): { intent: string; confidence: number } {
    const lowerMessage = message.toLowerCase();
    
    const intents = [
        { intent: 'upload_positions', keywords: ['upload', 'import', 'positions', 'file', 'excel', 'csv', 'data'], weight: 0 },
        { intent: 'upload_budgets', keywords: ['upload', 'import', 'budget', 'budgets'], weight: 0 },
        { intent: 'view_positions', keywords: ['view', 'show', 'list', 'positions', 'display'], weight: 0 },
        { intent: 'help', keywords: ['help', 'what can', 'how do', 'assist'], weight: 0 },
        { intent: 'greeting', keywords: ['hello', 'hi', 'hey', 'good morning', 'good afternoon'], weight: 0 },
    ];

    for (const intentObj of intents) {
        for (const keyword of intentObj.keywords) {
            if (lowerMessage.includes(keyword)) {
                intentObj.weight += 1;
            }
        }
    }

    const bestIntent = intents.reduce((a, b) => a.weight > b.weight ? a : b);
    return { 
        intent: bestIntent.weight > 0 ? bestIntent.intent : 'unknown', 
        confidence: bestIntent.weight / bestIntent.keywords.length 
    };
}

// Fuzzy string matching
function fuzzyMatch(searchTerm: string, target: string): number {
    if (searchTerm === undefined || searchTerm === null || searchTerm === '' || !target) return 0;

    const search = String(searchTerm).toLowerCase().trim();
    const targetLower = String(target).toLowerCase().trim();
    if (!search || !targetLower) return 0;
    
    // Exact match
    if (search === targetLower) return 1.0;
    
    // Contains match
    if (targetLower.includes(search) || search.includes(targetLower)) return 0.8;
    
    // Word-based matching
    const searchWords = search.split(/[\s&,]+/).filter(w => w.length > 1);
    const targetWords = targetLower.split(/[\s&,]+/).filter(w => w.length > 1);
    
    let matchCount = 0;
    for (const searchWord of searchWords) {
        for (const targetWord of targetWords) {
            if (targetWord.includes(searchWord) || searchWord.includes(targetWord)) {
                matchCount++;
                break;
            }
        }
    }
    
    if (searchWords.length > 0) {
        const wordScore = matchCount / Math.max(searchWords.length, targetWords.length);
        if (wordScore > 0.5) return wordScore * 0.7;
    }
    
    // Prefix matching
    if (targetLower.startsWith(search.substring(0, 3)) || search.startsWith(targetLower.substring(0, 3))) {
        return 0.4;
    }
    
    return 0;
}

function findBestMatch<T extends { [key: string]: any }>(
    searchTerm: string,
    items: T[],
    nameField: string
): { item: T | undefined; score: number } {
    let bestMatch: T | undefined;
    let bestScore = 0;
    
    for (const item of items) {
        const score = fuzzyMatch(searchTerm, item[nameField] || '');
        if (score > bestScore) {
            bestScore = score;
            bestMatch = item;
        }
    }
    
    return { item: bestScore >= 0.4 ? bestMatch : undefined, score: bestScore };
}

// Normalize a column or alias name for lazy comparison: splits camelCase /
// PascalCase boundaries, lowercases, and collapses common separators
// (_, -, ., /, parens, asterisks, etc.) into single spaces. So "Trade_Date",
// "trade-date", "tradeDate", "TRADE.DATE*", and "  Trade Date " all reduce
// to the same string "trade date".
function normalizeColumnName(name: string): string {
    return String(name ?? '')
        // Split camelCase / PascalCase boundaries first so "tradeDate" becomes
        // "trade Date" and "POBox" becomes "PO Box".
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
        .toLowerCase()
        .replace(/[*\u00d7]/g, '')
        .replace(/[_\-./()\\\[\]{}:;,#@!?]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// Split a normalized name into tokens and de-pluralize common trailing 's' so
// "volumes" matches "volume", "contracts" matches "contract", etc.
function tokenizeColumnName(normalized: string): string[] {
    return normalized.split(' ')
        .filter(w => w.length > 0)
        .map(w => (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) ? w.slice(0, -1) : w);
}

// Score how well a file column matches a known alias on a [0, 1] scale.
// Higher = stronger confidence. 0 means no match at all.
function scoreColumnAlias(columnName: string, alias: string): number {
    const colNorm = normalizeColumnName(columnName);
    const aliNorm = normalizeColumnName(alias);
    if (!colNorm || !aliNorm) return 0;

    // 1.0 - exact normalized match (handles case, separators, asterisks).
    if (colNorm === aliNorm) return 1.0;

    const colTokens = tokenizeColumnName(colNorm);
    const aliTokens = tokenizeColumnName(aliNorm);
    if (colTokens.length === 0 || aliTokens.length === 0) return 0;

    const colSet = new Set(colTokens);
    const aliSet = new Set(aliTokens);

    // 0.95 - same set of tokens (different order and/or plural form).
    if (colSet.size === aliSet.size && aliTokens.every(t => colSet.has(t))) {
        return 0.95;
    }

    // 0.85 - every alias token is contained in the column (alias is a subset).
    // Restricted to multi-token aliases so short aliases like "Date" or "Type"
    // don't wrongly grab columns such as "Settlement Date" or "Account Type".
    if (aliTokens.length >= 2 && aliTokens.every(t => colSet.has(t))) {
        return 0.85;
    }

    // 0.8 - every column token is contained in the alias (column is a subset).
    // Same multi-token guard.
    if (colTokens.length >= 2 && colTokens.every(t => aliSet.has(t))) {
        return 0.8;
    }

    return 0;
}

// Check if a file column maps to a known field. Uses lazy matching so columns
// like "Trade_Date", "TRADE-DATE *", or "Quantity Bushels" auto-map without
// the user having to open the column-mapping dialog.
function getKnownFieldForColumn(columnName: string): string | null {
    const MIN_SCORE = 0.8;
    let bestField: string | null = null;
    let bestScore = 0;

    for (const [field, aliases] of Object.entries(KNOWN_FIELD_MAPPINGS)) {
        for (const alias of aliases) {
            const score = scoreColumnAlias(columnName, alias);
            if (score > bestScore) {
                bestScore = score;
                bestField = field;
                if (bestScore >= 1) return bestField; // can't beat an exact match
            }
        }
    }

    return bestScore >= MIN_SCORE ? bestField : null;
}

// Adapts a parsed import row onto the shared duplicate-check shape. The rule itself
// lives in src/Helpers/PositionDuplicateHelper.ts so other import flows can reuse it.
function toDuplicateCandidate(row: ParsedPositionRow): DuplicateCheckPosition {
    return {
        tradeDate: row.data.tradeDate,
        volume: row.data.volume,
        price: row.data.price,
        unit: row.data.unit,
        side: row.data.side,
        commodityId: row.mappedData.commodity?.commodityId,
        counterpartyId: row.mappedData.counterparty?.counterpartyId,
        budgetDate: row.data.budgetDate,
        contractDate: row.data.contractDate,
    };
}

// Validation helper
function validateRow(
    row: ParsedPositionRow,
    customerData: GetCustomerDataResponse,
    existingPositions: PositionModel[] | undefined
): { errors: { field: string; message: string }[]; warnings: { field: string; message: string }[] } {
    const errors: { field: string; message: string }[] = [];
    const warnings: { field: string; message: string }[] = [];

    if (!row.mappedData.commodity) {
        errors.push({ field: 'commodity', message: 'Required' });
    }
    if (!row.mappedData.counterparty) {
        errors.push({ field: 'counterparty', message: 'Required' });
    }
    if (!row.mappedData.location) {
        errors.push({ field: 'location', message: 'Required' });
    }
    if (!row.data.volume && row.data.volume !== 0) {
        errors.push({ field: 'volume', message: 'Required' });
    }
    if (!row.data.budgetDate?.month || !row.data.budgetDate?.year) {
        errors.push({ field: 'budgetDate', message: 'Required' });
    }
    if (!row.data.price && row.data.price !== 0) {
        warnings.push({ field: 'price', message: 'No price' });
    }

    if (findDuplicatePosition(toDuplicateCandidate(row), existingPositions)) {
        warnings.push({
            field: 'duplicate',
            message: 'Position already exists (matching trade date, volume, price, counterparty, '
                + 'budget month/year, contract month/year, side and commodity)',
        });
    }

    return { errors, warnings };
}

export function ChatAssistant(props: ChatAssistantProps) {
    const { onDataRefresh } = props;
    const { enqueueSnackbar } = useSnackbar();

    // Open/dock state lives in ChatUiContext so the header button, the drawer and the
    // full-page route can all drive it. This component stays mounted either way, which
    // is what keeps the conversation alive across a dock-mode switch.
    const { isOpen: open, isFullPage, mode, fullPageSlot, closeChat: onClose, setMode } = useChatUi();

    // Used for the "edit this row" actions inside embedded result tables: in drawer
    // mode they dismiss the drawer before navigating, but on the full page the row
    // action does its own navigation and closing here would double-navigate.
    const closeDrawerForNavigation = useCallback(() => {
        if (!isFullPage) onClose();
    }, [isFullPage, onClose]);
    
    const customerData = useApplicationSelector((state: RootState): GetCustomerDataResponse => 
        state.commodityManagement.customerData
    );
    const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse | undefined =>
        state.commodityManagement.customerSettings
    );
    const navigate = useNavigate();
    const { setActiveResult } = useChatResults();

    const activeSiteGuid = useApplicationSelector((state: RootState): string | undefined =>
        state.activeSite.site?.siteGuid
    );

    // Budget imports need the site's pricing strategies to derive pricingStrategyId,
    // which is required on UpdateBudgetModel but never present in an uploaded file.
    // The assistant lives in the header, so it outlives site changes: reload whenever
    // the active site changes and never call without one, otherwise the request goes
    // out with no X-Site-Guid and the strategies offered belong to a different site.
    const [pricingStrategies, setPricingStrategies] = useState<BudgetPricingStrategyModel[]>([]);
    useEffect(() => {
        setPricingStrategies([]);
        if (!activeSiteGuid) {
            return;
        }

        let cancelled = false;
        CommodityManagementApi.listPricingStrategies(null)
            .then(response => {
                if (!cancelled) setPricingStrategies(response.data.rows ?? []);
            })
            .catch(error => console.error('Failed to load pricing strategies', error));
        return () => { cancelled = true; };
    }, [activeSiteGuid]);

    const importContext = useMemo<ImportContext>(
        () => ({ customerData, customerSettings, pricingStrategies, siteGuid: activeSiteGuid }),
        [customerData, customerSettings, pricingStrategies, activeSiteGuid]
    );

    const [messages, setMessages] = useState<ChatMessage[]>([
        {
            id: '1',
            role: 'assistant',
            content: 'What can I help you with today? I can assist you with:\n\n• **Upload Positions** - Upload position data from Excel/CSV files\n• **Upload Budgets** - Import budget data\n• **View Data** - Display your positions or budgets\n\nYou can also attach a file and tell me what you\'d like to do with it.',
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    // A purchase confirmation being reviewed in the side pane. Word contracts take the
    // EFP path rather than the spreadsheet import wizard.
    const [efpContractFile, setEfpContractFile] = useState<File | null>(null);
    const [parsedRows, setParsedRows] = useState<ParsedPositionRow[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [showDataReview, setShowDataReview] = useState(false);
    // Set when a single-sheet upload is being handled by the generic import wizard
    // (model picker -> full column mapping -> Handsontable preview). The legacy
    // parsedRows path below still serves multi-tab trade workbooks until that parser is
    // converged onto the same pipeline.
    const [wizardData, setWizardData] = useState<{
        fileName: string;
        rawRows: Record<string, unknown>[];
        sheetNames?: string[];
    } | null>(null);

    // Bridge the existing localStorage mapping store into the wizard. Moving this to a
    // per-site backend store is the next step (it needs no LLM); keeping the lookup
    // behind these two callbacks means that swap does not touch the wizard.
    const savedMappingsForWizard = useMemo<Record<string, string | null> | undefined>(() => {
        if (!wizardData) return undefined;
        const columns = getColumnUnion(wizardData.rawRows);
        const saved = findSavedMappingForFile(wizardData.fileName, columns);
        return saved?.columnMappings;
    }, [wizardData]);

    const handlePersistWizardMappings = useCallback((_modelKey: string, mappings: Record<string, string>) => {
        if (!wizardData) return;
        const columns = getColumnUnion(wizardData.rawRows);
        recordSavedMapping(wizardData.fileName, columns, mappings);
    }, [wizardData]);
    
    // Column mapping state
    const [unmappedColumns, setUnmappedColumns] = useState<UnmappedColumn[]>([]);
    const [columnMappings, setColumnMappings] = useState<ColumnMapping>({});
    const [showMappingDialog, setShowMappingDialog] = useState(false);
    const [rawFileData, setRawFileData] = useState<any[]>([]);
    // Name of the file currently being mapped/reviewed. We hold this separately from
    // pendingFile because pendingFile is cleared as soon as parsing starts, but we
    // still need the name later when the user applies the mappings (so we can save
    // them keyed by file name for future uploads).
    const [currentFileName, setCurrentFileName] = useState<string>('');
    // Saved mappings management dialog state.
    const [showSavedMappingsDialog, setShowSavedMappingsDialog] = useState(false);
    const [savedMappings, setSavedMappings] = useState<SavedFileMapping[]>([]);

    // Existing positions used for duplicate detection on the review page.
    const [existingPositions, setExistingPositions] = useState<PositionModel[]>([]);
    // Active sheet tab when reviewing a multi-tab workbook.
    const [selectedSheet, setSelectedSheet] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesTopRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // The list renders newest first, so the newest message sits at the top.
    const scrollToNewest = () => {
        messagesTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };
    
    useEffect(() => {
        scrollToNewest();
    }, [messages]);

    // Revalidate rows when they change
    const validatedRows = useMemo(() => {
        return parsedRows.map(row => {
            const { errors, warnings } = validateRow(row, customerData, existingPositions);
            return { ...row, errors, warnings };
        });
    }, [parsedRows, customerData, existingPositions]);

    const errorCount = validatedRows.filter(r => r.errors.length > 0).length;
    const warningCount = validatedRows.filter(r => r.warnings.length > 0 && r.errors.length === 0).length;
    const validCount = validatedRows.filter(r => r.errors.length === 0).length;

    // Group rows by their originating worksheet so each tab can be reviewed independently.
    // Rows without a sheetName (legacy single-sheet flow) are bucketed under "All".
    const sheetNames = useMemo(() => {
        const order: string[] = [];
        const seen = new Set<string>();
        for (const r of validatedRows) {
            const name = r.sheetName || 'All';
            if (!seen.has(name)) {
                seen.add(name);
                order.push(name);
            }
        }
        return order;
    }, [validatedRows]);

    useEffect(() => {
        if (sheetNames.length === 0) return;
        if (!sheetNames.includes(selectedSheet)) {
            setSelectedSheet(sheetNames[0]);
        }
    }, [sheetNames, selectedSheet]);

    const visibleRows = useMemo(() => {
        if (sheetNames.length <= 1) return validatedRows;
        return validatedRows.filter(r => (r.sheetName || 'All') === selectedSheet);
    }, [validatedRows, sheetNames, selectedSheet]);

    // Split out rows that already exist in the database so they don't appear in the
    // editable table and aren't uploaded. They're shown in a separate collapsed section.
    const isDuplicateRow = (r: ParsedPositionRow) => r.warnings.some(w => w.field === 'duplicate');
    const editableRows = useMemo(() => visibleRows.filter(r => !isDuplicateRow(r)), [visibleRows]);
    const duplicateRows = useMemo(() => visibleRows.filter(r => isDuplicateRow(r)), [visibleRows]);
    const [showDuplicates, setShowDuplicates] = useState(false);

    // Reset to the first page whenever the active sheet changes or the row set shrinks
    // below the current page window. Keeps the DOM cost bounded so switching tabs is fast.
    useEffect(() => {
        setPage(0);
        setShowDuplicates(false);
    }, [selectedSheet]);

    const pagedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return editableRows.slice(start, start + rowsPerPage);
    }, [editableRows, page, rowsPerPage]);

    // Fetch existing positions in the trade-date range of the given rows so we can warn
    // the user about duplicates (same trade date, volume, price, contract month/year).
    // Called explicitly when a file is first parsed; intentionally NOT bound to parsedRows
    // changes so per-row edits don't cause re-fetches and re-renders that freeze the UI.
    const loadExistingPositionsForRows = async (rows: ParsedPositionRow[]) => {
        if (rows.length === 0) {
            setExistingPositions([]);
            return;
        }
        const tradeDates = rows
            .map(r => r.data.tradeDate)
            .filter((d): d is string => !!d)
            .map(d => dayjs(d))
            .filter(d => d.isValid());
        if (tradeDates.length === 0) {
            setExistingPositions([]);
            return;
        }
        const minDate = tradeDates.reduce((a, b) => (a.isBefore(b) ? a : b)).format('YYYY-MM-DD');
        const maxDate = tradeDates.reduce((a, b) => (a.isAfter(b) ? a : b)).format('YYYY-MM-DD');
        try {
            const response = await CommodityManagementApi.listPositions(
                null,
                undefined, // quoteDate
                false, // includeExpirationDates
                false, // includeMarketPrices
                false, // useDisplayUnits
                undefined, // scenarioMarketPrices
                undefined, // commoditiesFilter
                undefined, // locationsFilter
                undefined, // counterpartiesFilter
                minDate,   // tradeDateStartFilter
                maxDate,   // tradeDateEndFilter
                undefined, undefined, undefined,   // budgetMonth start year/month/label
                undefined, undefined, undefined,   // budgetMonth end year/month/label
                undefined, undefined, undefined,   // contractMonth start year/month/label
                undefined, undefined, undefined,   // contractMonth end year/month/label
                undefined, // positionTypesFilter
                undefined, // dealTicketNumberFilter
                undefined, // dealTicketIdFilter
                undefined, // sideFilter
                undefined, // validatedFilter
                undefined, // currencyFilter
                undefined, // currency
                undefined, // expiryStatus
                false,     // includeAllocations
                false,     // includeAllocationOffset
                true,      // filterByTradeDate
            );
            setExistingPositions(response.data.rows ?? []);
        } catch (err) {
            console.error('Failed to load existing positions for duplicate check', err);
            setExistingPositions([]);
        }
    };

    const addMessage = (role: 'user' | 'assistant', content: string) => {
        const newMessage: ChatMessage = {
            id: Date.now().toString(),
            role,
            content,
            timestamp: new Date()
        };
        setMessages(prev => [...prev, newMessage]);
        return newMessage;
    };

    // Push an assistant message that also carries a rich React result (e.g.
    // a PositionTable rendered from a chat query). The text appears first
    // and the element renders below it inside the same bubble.
    const addResultMessage = (content: string, resultElement: React.ReactNode, resultPayload?: ChatResultPayload) => {
        const newMessage: ChatMessage = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            role: 'assistant',
            content,
            timestamp: new Date(),
            resultElement,
            resultPayload,
        };
        setMessages(prev => [...prev, newMessage]);
        return newMessage;
    };

    // Stash the structured result in shared context and navigate to the
    // full-page view. The drawer is intentionally left open so the chat
    // thread stays anchored on the right beside the wide table.
    const handleOpenResultInFullPage = (payload: ChatResultPayload) => {
        setActiveResult(payload);
        navigate('/commodity-management/chat-results');
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setPendingFile(file);
            addMessage('user', `I've attached a file: **${file.name}**`);
            addMessage('assistant', `I see you've uploaded **${file.name}**. What would you like me to do with this file?\n\nFor example:\n• "Upload these as positions"\n• "Import this data"\n• "Parse and validate this file"`);
        }
        // Reset the input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const analyzeFileColumns = (jsonData: any[]): { mappedColumns: string[], unmapped: UnmappedColumn[] } => {
        if (jsonData.length === 0) return { mappedColumns: [], unmapped: [] };
        
        const allColumns = Object.keys(jsonData[0]);
        const mappedColumns: string[] = [];
        const unmapped: UnmappedColumn[] = [];
        
        for (const column of allColumns) {
            const knownField = getKnownFieldForColumn(column);
            if (knownField) {
                mappedColumns.push(column);
            } else {
                // Get sample values for this column
                const sampleValues = jsonData
                    .slice(0, 3)
                    .map(row => String(row[column] ?? ''))
                    .filter(v => v !== '');
                
                unmapped.push({
                    originalName: column,
                    sampleValues,
                    mappedTo: undefined
                });
            }
        }
        
        return { mappedColumns, unmapped };
    };

    const parseExcelFileWithMappings = (jsonData: any[], customMappings: ColumnMapping): ParsedPositionRow[] => {
        if (jsonData.length === 0) return [];

        // Resolve each file column to a known field once for the whole dataset
        // (all rows share the same header set). knownByColumn uses the lazy
        // matcher so variations like "Trade_Date" / "TRADE-DATE *" auto-map.
        // customByColumn captures explicit user overrides from the mapping dialog.
        const allCols = Object.keys(jsonData[0] || {});
        const knownByColumn: Record<string, string> = {};
        const customByColumn: Record<string, string> = {};
        for (const col of allCols) {
            const customTarget = customMappings[col];
            if (customTarget && customTarget !== 'ignore') {
                customByColumn[col] = customTarget;
            }
            const knownField = getKnownFieldForColumn(col);
            if (knownField) {
                knownByColumn[col] = knownField;
            }
        }

        return jsonData.map((row, index) => {
            // Look up a value by target field. Known mappings win over custom
            // mappings (matching the pre-lazy-match behavior); within each group
            // the first non-undefined column value is returned.
            const getValue = (key: string) => {
                for (const [col, field] of Object.entries(knownByColumn)) {
                    if (field === key) {
                        const v = (row as any)[col];
                        if (v !== undefined) return v;
                    }
                }
                for (const [col, field] of Object.entries(customByColumn)) {
                    if (field === key) {
                        const v = (row as any)[col];
                        if (v !== undefined) return v;
                    }
                }
                return undefined;
            };

            // Helper to parse a date value and extract month/year
            const parseDateValue = (value: any): { month: number; year: number } | null => {
                if (!value) return null;
                
                let date: dayjs.Dayjs;
                if (typeof value === 'number') {
                    // Excel serial date
                    date = dayjs(new Date(Math.round((value - 25569) * 86400 * 1000)));
                } else {
                    date = dayjs(value);
                }
                
                if (date.isValid()) {
                    return { month: date.month() + 1, year: date.year() }; // dayjs months are 0-indexed
                }
                return null;
            };

            // Helper to get month/year from either a numeric value or a date
            const getMonthYearFromValue = (monthKey: string, yearKey: string): { month: number; year: number } => {
                const monthValue = getValue(monthKey);
                const yearValue = getValue(yearKey);

                // If we have explicit month and year values, try to combine them.
                if (monthValue !== undefined && yearValue !== undefined) {
                    const parsedYear = Number(yearValue);
                    if (!isNaN(parsedYear)) {
                        // Numeric month (e.g. 1..12)
                        const parsedMonth = Number(monthValue);
                        if (!isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
                            return { month: parsedMonth, year: parsedYear };
                        }
                        // Month name string (e.g. "Jan", "January") paired with a numeric year column
                        if (typeof monthValue === 'string') {
                            const monthFromName = monthToNumber(monthValue);
                            if (monthFromName !== undefined) {
                                return { month: monthFromName, year: parsedYear };
                            }
                        }
                    }
                }

                // Check if month value is actually a date we can parse
                if (monthValue !== undefined) {
                    const dateFromMonth = parseDateValue(monthValue);
                    if (dateFromMonth) {
                        return dateFromMonth;
                    }
                }

                // Check if year value is actually a date we can parse
                if (yearValue !== undefined) {
                    const dateFromYear = parseDateValue(yearValue);
                    if (dateFromYear) {
                        return dateFromYear;
                    }
                }

                // Return defaults
                return { month: 1, year: dayjs().year() };
            };
            
            const commodityName = getValue('commodity');
            const counterpartyName = getValue('counterparty');
            const locationName = getValue('location');
            const portfolioName = getValue('portfolio');
            
            const commodityMatch = findBestMatch(commodityName, customerData?.commodities || [], 'commodityName');
            const counterpartyMatch = findBestMatch(counterpartyName, customerData?.counterparties || [], 'counterpartyName');
            const locationMatch = findBestMatch(locationName, customerData?.locations || [], 'locationName');
            const portfolioMatch = findBestMatch(portfolioName, customerData?.portfolios || [], 'portfolioName');
            
            const tradeDate = getValue('tradeDate');
            const parsedTradeDate = tradeDate
                ? typeof tradeDate === 'number'
                    ? new Date(Math.round((tradeDate - 25569) * 86400 * 1000)).toISOString().substring(0, 10)
                    : dayjs(tradeDate).format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD');

            const volume = getValue('volume') || 0;
            const price = getValue('price') || 0;
            
            // Get budget date (month/year)
            const budgetDate = getMonthYearFromValue('budgetMonth', 'budgetYear');
            
            // Check if contract month/year are explicitly mapped
            const hasContractMonthMapping = getValue('contractMonth') !== undefined;
            const hasContractYearMapping = getValue('contractYear') !== undefined;
            
            // If contract values are explicitly mapped, use them; otherwise default to budget values
            const contractDate = (hasContractMonthMapping || hasContractYearMapping)
                ? getMonthYearFromValue('contractMonth', 'contractYear')
                : { ...budgetDate };
            
            // Collect unmapped data
            const unmappedData: Record<string, any> = {};
            for (const [col, val] of Object.entries(row)) {
                if (!knownByColumn[col] && !customByColumn[col]) {
                    unmappedData[col] = val;
                }
            }
            
            return {
                rowNumber: index + 2,
                id: `row_${index}_${Date.now()}`,
                data: {
                    commodityName,
                    counterpartyName,
                    locationName,
                    portfolioName,
                    tradeDate: parsedTradeDate,
                    dealTicketNumber: getValue('dealTicketNumber') || '',
                    volume: Number(volume) || 0,
                    price: Number(price) || 0,
                    unit: (getValue('unit') || Unit.Bu) as Unit,
                    currency: (getValue('currency') || Currency.Usd) as Currency,
                    side: (getValue('side') || Side.Buy) as Side,
                    positionType: (getValue('positionType') || PositionType.Future) as PositionType,
                    budgetDate: { month: budgetDate.month, year: budgetDate.year } as ContractDate,
                    contractDate: { month: contractDate.month, year: contractDate.year } as ContractDate,
                    optionStrike: getValue('optionStrike') !== undefined && getValue('optionStrike') !== '' ? Number(getValue('optionStrike')) : undefined,
                    optionType: (getValue('optionType') || undefined) as OptionType | undefined,
                    optionStyle: (getValue('optionStyle') || undefined) as OptionStyle | undefined,
                },
                mappedData: {
                    commodity: commodityMatch.item,
                    counterparty: counterpartyMatch.item,
                    location: locationMatch.item,
                    portfolio: portfolioMatch.item
                },
                unmappedData,
                errors: [],
                warnings: []
            };
        });
    };

    const parseExcelFile = async (file: File): Promise<{ jsonData: any[], unmapped: UnmappedColumn[] }> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const data = new Uint8Array(event.target?.result as ArrayBuffer);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    
                    // Get raw data as array of arrays to inspect headers
                    const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[];
                    
                    if (rawData.length < 2) {
                        resolve({ jsonData: [], unmapped: [] });
                        return;
                    }
                    
                    // Check if we have multi-row headers
                    const { hasMultiRowHeader, headerRowCount, mergedHeaders } = detectMultiRowHeaders(rawData);
                    
                    let jsonData: any[];
                    
                    if (hasMultiRowHeader) {
                        // Build JSON manually using merged headers
                        jsonData = [];
                        for (let i = headerRowCount; i < rawData.length; i++) {
                            const row = rawData[i] as any[];
                            // Skip empty rows
                            if (!row || !Array.isArray(row) || row.every(cell => cell === undefined || cell === null || cell === '')) {
                                continue;
                            }
                            
                            const rowObj: Record<string, any> = {};
                            for (let j = 0; j < mergedHeaders.length; j++) {
                                const header = mergedHeaders[j];
                                if (header) {
                                    rowObj[header] = row[j];
                                }
                            }
                            jsonData.push(rowObj);
                        }
                    } else {
                        // Standard single-row header parsing
                        jsonData = XLSX.utils.sheet_to_json(worksheet) as any[];
                    }
                    
                    const { unmapped } = analyzeFileColumns(jsonData);
                    
                    resolve({ jsonData, unmapped });
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsArrayBuffer(file);
        });
    };

    // Helper function to detect and merge multi-row headers
    function detectMultiRowHeaders(rawData: any[][]): { 
        hasMultiRowHeader: boolean; 
        headerRowCount: number; 
        mergedHeaders: string[] 
    } {
        if (rawData.length < 2) {
            return { hasMultiRowHeader: false, headerRowCount: 1, mergedHeaders: [] };
        }
        
        const row1 = rawData[0] || [];
        const row2 = rawData[1] || [];
        const row3 = rawData[2]; // First potential data row
        
        // Heuristics to detect if row 2 is a header continuation:
        // 1. Row 2 has text values that look like header suffixes (Date, Month, Type, etc.)
        // 2. Row 2 values are mostly strings (not numbers or dates that look like data)
        // 3. Row 3 (if exists) looks more like data (contains numbers, dates)
        
        const headerSuffixPatterns = [
            /^(date|month|year|type|name|id|number|code|price|volume|qty|quantity|bushels|contracts|rate|amount)$/i
        ];

        let row2LooksLikeHeaderContinuation = false;
        let suffixMatchCount = 0;
        let row2StringCount = 0;
        let row2EmptyCount = 0;
        let row2NumericCount = 0;

        for (let i = 0; i < Math.max(row1.length, row2.length); i++) {
            const cell2 = row2[i];

            if (cell2 === undefined || cell2 === null || cell2 === '') {
                row2EmptyCount++;
                continue;
            }

            // Numeric values (including numeric strings like "2026" or "4.65") are
            // strong evidence that row2 is data, not a header continuation. Header
            // words are essentially never raw numbers.
            if (typeof cell2 === 'number') {
                row2NumericCount++;
                continue;
            }
            if (typeof cell2 === 'string') {
                const trimmed = cell2.trim();
                if (trimmed !== '' && !isNaN(Number(trimmed))) {
                    row2NumericCount++;
                    continue;
                }
                row2StringCount++;
                // Check if it matches common header suffix patterns
                if (headerSuffixPatterns.some(pattern => pattern.test(trimmed))) {
                    suffixMatchCount++;
                }
            }
        }

        // Check if row 3 looks like data (has numbers)
        let row3HasNumericData = false;
        if (row3) {
            for (const cell of row3 as any[]) {
                if (typeof cell === 'number' || 
                    (typeof cell === 'string' && !isNaN(parseFloat(cell)) && cell.trim() !== '')) {
                    row3HasNumericData = true;
                    break;
                }
            }
        }

        // Determine if we have multi-row headers. Be conservative - false positives
        // here destroy lazy matching by gluing header text to first-row data values
        // (e.g. "Commodity* Corn"). A true header continuation row should:
        //   (a) contain NO numeric cells (real headers aren't numbers), AND
        //   (b) have at least 2 cells that look like header-suffix words OR
        //       be almost entirely header-suffix words with data below.
        const nonEmptyRow2Count = row2.length - row2EmptyCount;
        const onlyHeaderWords = row2NumericCount === 0
            && row2StringCount > 0
            && suffixMatchCount >= Math.max(2, Math.floor(nonEmptyRow2Count * 0.6))
            && row3HasNumericData;
        row2LooksLikeHeaderContinuation =
            (row2NumericCount === 0 && suffixMatchCount >= 2) ||
            onlyHeaderWords;
        
        if (!row2LooksLikeHeaderContinuation) {
            return { hasMultiRowHeader: false, headerRowCount: 1, mergedHeaders: [] };
        }
        
        // Merge headers from row1 and row2
        const mergedHeaders: string[] = [];
        const maxCols = Math.max(row1.length, row2.length);
        
        for (let i = 0; i < maxCols; i++) {
            const h1 = row1[i] !== undefined && row1[i] !== null ? String(row1[i]).trim() : '';
            const h2 = row2[i] !== undefined && row2[i] !== null ? String(row2[i]).trim() : '';
            
            let merged: string;
            if (h1 && h2) {
                // Combine with space, e.g., "Trade" + "Date" = "Trade Date"
                merged = `${h1} ${h2}`;
            } else if (h1) {
                merged = h1;
            } else if (h2) {
                merged = h2;
            } else {
                merged = `Column${i + 1}`;
            }
            
            mergedHeaders.push(merged);
        }
        
        return { hasMultiRowHeader: true, headerRowCount: 2, mergedHeaders };
    }

    // Natural-language data retrieval branch. Returns true when the message
    // was recognised as a data query and a response was pushed to the chat;
    // returns false so the caller can fall through to its next handler.
    //
    // Time-dimension rules (from user direction):
    //  - bare years are expanded to the customer's fiscal budget-month range
    //  - "trade date"/"traded on"/"trades from..." switches to tradeDate
    //
    // Edit actions inside embedded result tables navigate to the full page.
    const handleQueryMessage = async (userMessage: string): Promise<boolean> => {
        const trimmed = userMessage.trim();
        if (!trimmed) return false;
        if (!customerSettings) return false;

        const parsed = parseChatQuery(trimmed, customerData, customerSettings);
        if (!parsed) return false;

        const { descriptor, filters, fiscalYearRanges, useTradeDate } = parsed;

        // Build a human echo of the filters so the user understands what
        // the assistant interpreted from the message.
        const filterBits: string[] = [];
        if (filters.commodityNames?.length) filterBits.push(`commodity **${filters.commodityNames.join(', ')}**`);
        if (filters.locationNames?.length) filterBits.push(`location **${filters.locationNames.join(', ')}**`);
        if (filters.counterpartyNames?.length) filterBits.push(`counterparty **${filters.counterpartyNames.join(', ')}**`);
        if (filters.portfolioNames?.length) filterBits.push(`portfolio **${filters.portfolioNames.join(', ')}**`);
        if (fiscalYearRanges.length > 0) {
            const fyDescriptions = fiscalYearRanges.map(r => describeFiscalYearRange(r));
            filterBits.push(useTradeDate
                ? `trade year **${filters.fiscalYears?.join(', ')}**`
                : `**${fyDescriptions.join(', ')}**`);
        } else if (filters.useTradeDate && (filters.tradeDateStart || filters.tradeDateEnd)) {
            filterBits.push(`trade date **${filters.tradeDateStart ?? '...'} → ${filters.tradeDateEnd ?? '...'}**`);
        }
        if (filters.positionTypes?.length) filterBits.push(`type **${filters.positionTypes.join(', ')}**`);
        if (filters.side) filterBits.push(`side **${filters.side}**`);

        const filterEcho = filterBits.length > 0
            ? `Filtering by ${filterBits.join(', ')}.`
            : `No specific filters detected - returning all ${descriptor.plural} I can see.`;

        addMessage('assistant', `Fetching ${descriptor.plural}... ${filterEcho}`);

        try {
            const rows = await descriptor.execute(filters, {
                customerSettings,
                navigate,
                closeDrawer: closeDrawerForNavigation,
            });

            if (!rows || rows.length === 0) {
                addMessage('assistant', descriptor.emptyMessage?.(filters) ?? `I didn't find any ${descriptor.plural}.`);
                return true;
            }

            // Optional one-line aggregate so the user sees totals without
            // scanning the embedded table. Positions get volume + Buy/Sell
            // net; budgets/allocations get just a count.
            const spec: AggregateSpec = descriptor.key === 'positions'
                ? { countLabel: descriptor.plural, sumFields: ['volume'], netSignField: 'side' }
                : { countLabel: descriptor.plural };
            const aggregate = aggregateRows(rows, spec);
            const summary = describeAggregate(aggregate, spec);

            const element = descriptor.render(rows, {
                customerSettings,
                navigate,
                closeDrawer: closeDrawerForNavigation,
            });

            const payload: ChatResultPayload = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                descriptorKey: descriptor.key,
                rows,
                summary,
                filterEcho,
                timestamp: new Date(),
            };

            addResultMessage(summary, element, payload);
            return true;
        } catch (err) {
            console.error(`Chat query (${descriptor.key}) failed`, err);
            addMessage('assistant', `Sorry, I couldn't load ${descriptor.plural} right now. Please try again or open the full ${descriptor.plural} page.`);
            return true;
        }
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() && !pendingFile) return;
        
        const userMessage = inputValue.trim();
        setInputValue('');
        
        if (userMessage) {
            addMessage('user', userMessage);
        }
        
        setIsProcessing(true);
        
        try {
            const { intent } = detectIntent(userMessage);
            
            // A Word contract is an EFP purchase confirmation, not a spreadsheet: it goes
            // to the EFP panel regardless of how the message was phrased.
            if (pendingFile && isEfpContractFile(pendingFile)) {
                setEfpContractFile(pendingFile);
                setPendingFile(null);
                setShowDataReview(true);
                addMessage('assistant',
                    `Reading **${pendingFile.name}** as an exchange for physical contract. `
                    + `I'll show you what it says and the positions it will write - nothing posts until you confirm.`);
                return;
            }

            if ((intent === 'upload_positions' || userMessage.toLowerCase().includes('upload') || userMessage.toLowerCase().includes('import')) && pendingFile) {
                addMessage('assistant', `Parsing **${pendingFile.name}**... Please wait.`);

                // Try the multi-tab trade-update format first (e.g. swap/future tabs per position type).
                // If recognised, each tab is parsed separately and routed to AddTrade.
                const multiTab = await parseMultiTabTradeFile(pendingFile, customerData);
                if (multiTab && multiTab.rows.length > 0) {
                    setParsedRows(multiTab.rows);
                    setShowDataReview(true);
                    setPendingFile(null);
                    loadExistingPositionsForRows(multiTab.rows);

                    const processed = multiTab.processedTabs
                        .map(t => `• **${t.name}** → ${t.positionType} (${t.rowCount} rows)`)
                        .join('\n');
                    const skipped = multiTab.skippedTabs.length
                        ? `\n\nSkipped tabs: ${multiTab.skippedTabs.map(s => `"${s}"`).join(', ')}`
                        : '';
                    addMessage('assistant',
                        `I recognised this as a multi-tab trade update file. Parsed **${multiTab.rows.length} rows** across ${multiTab.processedTabs.length} tab(s):\n${processed}${skipped}\n\nPlease review and edit the data below, then click **Upload Positions** when ready.`);
                    return;
                }

                const { jsonData } = await parseExcelFile(pendingFile);
                setRawFileData(jsonData);
                setCurrentFileName(pendingFile.name);

                if (jsonData.length === 0) {
                    addMessage('assistant', `I couldn't read any rows out of **${pendingFile.name}**. Check that the first sheet has a header row and at least one row of data.`);
                    setPendingFile(null);
                    return;
                }

                // Hand off to the generic import wizard: it works out which model the
                // file targets (asking when it isn't clear), shows every column and what
                // it maps to, and previews the data for editing before upload.
                setWizardData({ fileName: pendingFile.name, rawRows: jsonData });
                setShowDataReview(true);
                addMessage('assistant', `I've read **${jsonData.length} rows** from **${pendingFile.name}**. Check the columns and preview the data on the right, then upload when it looks right.`);
                setPendingFile(null);
                return;
            } else if (intent === 'greeting') {
                addMessage('assistant', 'Hello! How can I assist you today? Feel free to upload a file or ask me about positions, budgets, or other data.');
            } else if (intent === 'help') {
                addMessage('assistant', 'I can help you with:\n\n• **Upload Positions** - Attach an Excel/CSV file and I\'ll parse it, validate the data using fuzzy matching, and show you any issues before uploading.\n• **Upload Budgets** - Similar process for budget data.\n• **View Data** - Ask me to show positions or other information.\n\nTo upload data, simply attach a file using the 📎 button and tell me what to do with it.');
            } else if (pendingFile) {
                addMessage('assistant', `I have your file **${pendingFile.name}** ready. Would you like me to:\n\n• Parse it as **positions**?\n• Parse it as **budgets**?\n\nJust let me know!`);
            } else if (await handleQueryMessage(userMessage)) {
                // Query branch matched the message (positions/budgets/allocations
                // retrieval) - the helper already pushed the result message.
            } else {
                addMessage('assistant', 'I\'m not sure what you\'d like to do. You can:\n\n• Attach a file and ask me to upload it as positions\n• Ask for help to see what I can do\n• Ask me questions about your data');
            }
        } catch (error) {
            console.error('Error processing message:', error);
            addMessage('assistant', 'Sorry, I encountered an error processing your request. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleMappingChange = (columnName: string, targetField: string | null) => {
        setColumnMappings(prev => ({
            ...prev,
            [columnName]: targetField
        }));

        setUnmappedColumns(prev => prev.map(col => 
            col.originalName === columnName 
                ? { ...col, mappedTo: targetField || undefined }
                : col
        ));
    };

    // Save the FULL effective column-to-field map (lazy-detected + the user's
    // custom dialog choices) for the current file so the bookmark list reflects
    // exactly how the file is being interpreted - not just the manual overrides.
    // Called from every parse path (auto-apply, dialog-apply, skip, fully-lazy).
    const persistEffectiveMappings = (
        fileColumns: string[],
        customMappings: ColumnMapping,
        fileNameOverride?: string
    ) => {
        const fileName = fileNameOverride ?? currentFileName;
        if (!fileName || fileColumns.length === 0) return;
        const effective: ColumnMapping = {};
        for (const col of fileColumns) {
            const custom = customMappings[col];
            if (custom !== undefined && custom !== null && custom !== '') {
                // Includes 'ignore' on purpose so we remember the user's "skip this
                // column" decision and don't re-prompt on the next upload.
                effective[col] = custom;
                continue;
            }
            const known = getKnownFieldForColumn(col);
            if (known) {
                effective[col] = known;
            }
        }
        recordSavedMapping(fileName, fileColumns, effective);
    };

    const handleApplyMappings = () => {
        const parsed = parseExcelFileWithMappings(rawFileData, columnMappings);
        setParsedRows(parsed);
        setShowMappingDialog(false);
        setShowDataReview(true);
        loadExistingPositionsForRows(parsed);

        // Persist the user's choices (merged with lazy-detected ones) for next time.
        if (rawFileData.length > 0) {
            persistEffectiveMappings(Object.keys(rawFileData[0]), columnMappings);
        }

        const mappedCount = Object.values(columnMappings).filter(v => v && v !== 'ignore').length;
        const ignoredCount = Object.values(columnMappings).filter(v => v === 'ignore').length;

        let message = `I've applied your column mappings and parsed **${parsed.length} rows**.`;
        if (mappedCount > 0) {
            message += ` Mapped ${mappedCount} custom column(s).`;
        }
        if (ignoredCount > 0) {
            message += ` Ignored ${ignoredCount} column(s).`;
        }
        if (currentFileName) {
            message += ` I'll remember these mappings the next time you upload **${currentFileName}**.`;
        }
        message += '\n\nPlease review and edit the data below, then click **Upload Positions** when ready.';

        addMessage('assistant', message);
    };

    const handleSkipMappings = () => {
        // Set all unmapped columns to ignore
        const ignoreMappings: ColumnMapping = {};
        unmappedColumns.forEach(col => {
            ignoreMappings[col.originalName] = 'ignore';
        });

        const parsed = parseExcelFileWithMappings(rawFileData, ignoreMappings);
        setParsedRows(parsed);
        setShowMappingDialog(false);
        setShowDataReview(true);
        loadExistingPositionsForRows(parsed);

        // Persist the "ignore everything unknown" decision so re-uploads of the same
        // file don't keep prompting the user.
        if (rawFileData.length > 0) {
            persistEffectiveMappings(Object.keys(rawFileData[0]), ignoreMappings);
        }

        addMessage('assistant', `I've parsed **${parsed.length} rows** from your file, ignoring the unmapped columns.\n\nPlease review and edit the data below, then click **Upload Positions** when ready.`);
    };

    const handleRowUpdate = (rowId: string, field: string, value: any) => {
        setParsedRows(prev => prev.map(row => {
            if (row.id !== rowId) return row;

            const updatedRow = { ...row };
            
            if (field === 'commodity') {
                updatedRow.mappedData = { ...row.mappedData, commodity: value };
                updatedRow.data = { ...row.data, commodityName: value?.commodityName };
            } else if (field === 'counterparty') {
                updatedRow.mappedData = { ...row.mappedData, counterparty: value };
                updatedRow.data = { ...row.data, counterpartyName: value?.counterpartyName };
            } else if (field === 'location') {
                updatedRow.mappedData = { ...row.mappedData, location: value };
                updatedRow.data = { ...row.data, locationName: value?.locationName };
            } else if (field === 'portfolio') {
                updatedRow.mappedData = { ...row.mappedData, portfolio: value };
                updatedRow.data = { ...row.data, portfolioName: value?.portfolioName };
            } else if (field === 'volume' || field === 'price') {
                updatedRow.data = { ...row.data, [field]: Number(value) || 0 };
            } else if (field === 'budgetMonth') {
                updatedRow.data = { ...row.data, budgetDate: { ...row.data.budgetDate, month: Number(value) } };
            } else if (field === 'budgetYear') {
                updatedRow.data = { ...row.data, budgetDate: { ...row.data.budgetDate, year: Number(value) } };
            } else if (field === 'contractMonth') {
                updatedRow.data = { ...row.data, contractDate: { ...row.data.contractDate, month: Number(value) } };
            } else if (field === 'contractYear') {
                updatedRow.data = { ...row.data, contractDate: { ...row.data.contractDate, year: Number(value) } };
            } else {
                updatedRow.data = { ...row.data, [field]: value };
            }
            
            return updatedRow;
        }));
    };

    const handleDeleteRow = (rowId: string) => {
        setParsedRows(prev => prev.filter(row => row.id !== rowId));
    };

    // Add this function after handleDeleteRow
    const handleBulkUpdate = (field: string, value: any) => {
        setParsedRows(prev => prev.map(row => {
            const updatedRow = { ...row };
            
            if (field === 'commodity') {
                updatedRow.mappedData = { ...row.mappedData, commodity: value };
                updatedRow.data = { ...row.data, commodityName: value?.commodityName };
            } else if (field === 'counterparty') {
                updatedRow.mappedData = { ...row.mappedData, counterparty: value };
                updatedRow.data = { ...row.data, counterpartyName: value?.counterpartyName };
            } else if (field === 'location') {
                updatedRow.mappedData = { ...row.mappedData, location: value };
                updatedRow.data = { ...row.data, locationName: value?.locationName };
            } else if (field === 'portfolio') {
                updatedRow.mappedData = { ...row.mappedData, portfolio: value };
                updatedRow.data = { ...row.data, portfolioName: value?.portfolioName };
            } else if (field === 'side' || field === 'positionType' || field === 'unit' || field === 'currency') {
                updatedRow.data = { ...row.data, [field]: value };
            } else if (field === 'budgetMonth') {
                updatedRow.data = { ...row.data, budgetDate: { ...row.data.budgetDate, month: Number(value) } };
            } else if (field === 'budgetYear') {
                updatedRow.data = { ...row.data, budgetDate: { ...row.data.budgetDate, year: Number(value) } };
            } else if (field === 'contractMonth') {
                updatedRow.data = { ...row.data, contractDate: { ...row.data.contractDate, month: Number(value) } };
            } else if (field === 'contractYear') {
                updatedRow.data = { ...row.data, contractDate: { ...row.data.contractDate, year: Number(value) } };
            } else if (field === 'tradeDate') {
                updatedRow.data = { ...row.data, tradeDate: value };
            }
            
            return updatedRow;
        }));
    };

    const handleUploadPositions = async () => {
        // Exclude rows that the duplicate check flagged so we don't re-upload existing positions.
        const duplicateRows = validatedRows.filter(r => r.warnings.some(w => w.field === 'duplicate'));
        const validRows = validatedRows.filter(r => r.errors.length === 0 && !r.warnings.some(w => w.field === 'duplicate'));

        if (validRows.length === 0) {
            enqueueSnackbar('No valid rows to upload', { variant: 'error' });
            return;
        }
        
        setIsUploading(true);
        addMessage('assistant', `Uploading **${validRows.length}** positions...`);
        
        try {
            // Group by deal ticket number for batch upload
            const groupedByTicket = validRows.reduce((acc, row) => {
                const ticketKey = row.data.dealTicketNumber || `auto_${row.mappedData.counterparty?.counterpartyId}_${row.data.tradeDate}`;
                if (!acc[ticketKey]) {
                    acc[ticketKey] = [];
                }
                acc[ticketKey].push(row);
                return acc;
            }, {} as Record<string, ParsedPositionRow[]>);
            
            let successCount = 0;
            let errorCount = 0;
            
            for (const [ticketKey, rows] of Object.entries(groupedByTicket)) {
                try {
                    const firstRow = rows[0];
                    
                    const positions: UpdatePositionModel[] = rows.map(row => ({
                        positionId: undefined,
                        volume: row.data.volume,
                        price: row.data.price,
                        unit: row.data.unit,
                        currency: row.data.currency,
                        side: row.data.side,
                        positionType: row.data.positionType,
                        budgetDate: row.data.budgetDate,
                        contractDate: row.data.contractDate,
                        locationId: row.mappedData.location?.locationId,
                        optionStrike: row.data.optionStrike,
                        optionType: row.data.optionType,
                        optionStyle: row.data.optionStyle,
                    }));
                    
                    const dealTicket: UpdateDealTicketRequest = {
                        tradeDate: firstRow.data.tradeDate || dayjs().format('MM/DD/YYYY'),
                        commodityId: firstRow.mappedData.commodity!.commodityId,
                        counterpartyId: firstRow.mappedData.counterparty!.counterpartyId,
                        dealTicketNumber: firstRow.data.dealTicketNumber || undefined,
                        portfolioId: firstRow.mappedData.portfolio?.portfolioId,
                        positions
                    };
                    
                    await CommodityManagementApi.addTrade(null, dealTicket);
                    successCount += rows.length;
                } catch (err) {
                    console.error('Error uploading deal ticket:', err);
                    errorCount += rows.length;
                }
            }
            
            let resultMessage = `Upload complete!\n\n`;
            resultMessage += `✅ **${successCount}** positions uploaded successfully\n`;
            if (errorCount > 0) {
                resultMessage += `❌ **${errorCount}** positions failed\n`;
            }
            if (duplicateRows.length > 0) {
                resultMessage += `⏭️ **${duplicateRows.length}** positions skipped (already exist)\n`;
            }
            
            addMessage('assistant', resultMessage);
            setParsedRows([]);
            setShowDataReview(false);
            setRawFileData([]);
            setUnmappedColumns([]);
            setColumnMappings({});
            setExistingPositions([]);
            setSelectedSheet('');
            setCurrentFileName('');
            
            if (onDataRefresh) {
                onDataRefresh();
            }
            
            enqueueSnackbar(`Successfully uploaded ${successCount} positions`, { variant: 'success' });
        } catch (error) {
            console.error('Error uploading positions:', error);
            addMessage('assistant', 'An error occurred while uploading positions. Please try again.');
            enqueueSnackbar('Failed to upload positions', { variant: 'error' });
        } finally {
            setIsUploading(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    const handleCancelReview = () => {
        setParsedRows([]);
        setShowDataReview(false);
        setRawFileData([]);
        setUnmappedColumns([]);
        setColumnMappings({});
        setExistingPositions([]);
        setSelectedSheet('');
        setCurrentFileName('');
        addMessage('assistant', 'Data review cancelled. Feel free to upload another file or ask me anything else.');
    };

    // Saved-mappings management dialog handlers. The list is loaded fresh whenever
    // the dialog is opened so it reflects any mappings just persisted by the apply
    // flow above.
    const handleOpenSavedMappings = () => {
        setSavedMappings(loadSavedMappings());
        setShowSavedMappingsDialog(true);
    };

    const handleDeleteSavedMapping = (fileName: string) => {
        deleteSavedMapping(fileName);
        setSavedMappings(loadSavedMappings());
    };

    const handleClearAllSavedMappings = () => {
        clearAllSavedMappings();
        setSavedMappings([]);
    };

    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const years = Array.from({ length: 10 }, (_, i) => dayjs().year() - 2 + i);

    return (
        <>
            <ChatShell
                isFullPage={isFullPage}
                fullPageSlot={fullPageSlot}
                open={open}
                onClose={onClose}
                wide={showDataReview}
            >
                <>
                    {/* Header */}
                    <Box sx={{ 
                        p: 2, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                        backgroundColor: CHAT_COLORS.headerBackground,
                        color: CHAT_COLORS.headerText,
                        flexShrink: 0
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SmartToyIcon />
                            <Typography variant="h6">Data Assistant</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip title="Saved file mappings">
                                <IconButton onClick={handleOpenSavedMappings} sx={{ color: CHAT_COLORS.headerText }}>
                                    <BookmarksIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={mode === 'fullPage' ? 'Move to the side' : 'Expand to full page'}>
                                <IconButton
                                    onClick={() => setMode(mode === 'fullPage' ? 'drawer' : 'fullPage')}
                                    sx={{ color: CHAT_COLORS.headerText }}
                                >
                                    {mode === 'fullPage' ? <CloseFullscreenIcon /> : <OpenInFullIcon />}
                                </IconButton>
                            </Tooltip>
                            <IconButton onClick={onClose} sx={{ color: CHAT_COLORS.headerText }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Main Content Area */}
                    <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                        {/* Chat Section */}
                        <Box sx={{ 
                            width: showDataReview ? { xs: '100%', md: 350 } : '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRight: showDataReview ? 1 : 0,
                            borderColor: 'divider',
                            flexShrink: 0
                        }}>
                            {/* Pending File Indicator */}
                            {pendingFile && (
                                <Box sx={{ px: 2, py: 1, backgroundColor: CHAT_COLORS.highlight }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AttachFileIcon fontSize="small" />
                                        <Typography variant="body2" fontSize="0.85rem">{pendingFile.name}</Typography>
                                        <IconButton size="small" onClick={() => setPendingFile(null)}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            )}

                            {/* Input Area */}
                            <Box sx={{ 
                                p: 2, 
                                borderTop: 1, 
                                borderColor: 'divider',
                                display: 'flex',
                                gap: 1,
                                alignItems: 'flex-end',
                                flexShrink: 0
                            }}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    style={{ display: 'none' }}
                                    accept=".xlsx,.xls,.csv,.docx"
                                    onChange={handleFileSelect}
                                />
                                <IconButton 
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isProcessing}
                                    size="small"
                                >
                                    <AttachFileIcon />
                                </IconButton>
                                <TextField
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    placeholder="Type a message..."
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    disabled={isProcessing}
                                    size="small"
                                />
                                <IconButton
                                    sx={{ color: CHAT_COLORS.accent }}
                                    onClick={handleSendMessage}
                                    disabled={isProcessing || (!inputValue.trim() && !pendingFile)}
                                    size="small"
                                >
                                    <SendIcon />
                                </IconButton>
                            </Box>
                            {/* Messages */}
                            <Box sx={{ 
                                flexGrow: 1, 
                                overflow: 'auto', 
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2
                            }}>
                                <div ref={messagesTopRef} />
                                {/* Newest first, so the latest reply is visible without scrolling. */}
                                {[...messages].reverse().map((message) => (
                                    <Box
                                        key={message.id}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, maxWidth: '95%' }}>
                                            {message.role === 'assistant' && (
                                                <SmartToyIcon sx={{ mt: 0.5, fontSize: 20, color: CHAT_COLORS.accent }} />
                                            )}
                                            <Paper
                                                sx={{
                                                    p: 1.5,
                                                    backgroundColor: message.role === 'user' ? CHAT_COLORS.accent : CHAT_COLORS.assistantBubble,
                                                    color: message.role === 'user' ? 'white' : 'text.primary',
                                                    borderRadius: 2,
                                                }}
                                            >
                                                <Typography 
                                                    variant="body2" 
                                                    sx={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}
                                                    dangerouslySetInnerHTML={{ 
                                                        __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                                                    }}
                                                />
                                            </Paper>
                                            {message.role === 'user' && (
                                                <PersonIcon color="action" sx={{ mt: 0.5, fontSize: 20 }} />
                                            )}
                                        </Box>
                                        {message.resultElement && (
                                            // Rich result (e.g. positions/budgets/allocations table) from the
                                            // chat query branch. Rendered outside the speech bubble so the
                                            // embedded table can fill the available drawer width.
                                            <Box sx={{ width: '100%', mt: 1, pl: 3, pr: 1 }}>
                                                <Paper
                                                    variant="outlined"
                                                    // The drawer cap keeps a long table from swallowing the
                                                    // conversation; on the full page there is room to show more.
                                                    sx={{ p: 1, overflow: 'auto', maxHeight: isFullPage ? '65vh' : 480 }}
                                                >
                                                    {message.resultElement}
                                                </Paper>
                                                {/* Redundant on the full page - the table already has the
                                                    full width there, and navigating away would unmount the
                                                    portal target this chat is rendering into. */}
                                                {message.resultPayload && !isFullPage && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                        <Button
                                                            size="small"
                                                            startIcon={<OpenInNewIcon fontSize="small" />}
                                                            onClick={() => handleOpenResultInFullPage(message.resultPayload!)}
                                                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                        >
                                                            View in full page
                                                        </Button>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 3, fontSize: '0.7rem' }}>
                                            {dayjs(message.timestamp).format('h:mm A')}
                                        </Typography>
                                    </Box>
                                ))}
                                {isProcessing && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SmartToyIcon fontSize="small" sx={{ color: CHAT_COLORS.accent }} />
                                        <CircularProgress size={16} />
                                        <Typography variant="body2" color="text.secondary" fontSize="0.85rem">
                                            Processing...
                                        </Typography>
                                    </Box>
                                )}
                                <div ref={messagesEndRef} />
                            </Box>

                        </Box>

                        {/* Generic import wizard - model picker, full column mapping,
                            Handsontable preview. Handles single-sheet uploads. */}
                        {showDataReview && efpContractFile && (
                            <Box sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'auto',
                                backgroundColor: CHAT_COLORS.panel
                            }}>
                                <EfpContractChatPanel
                                    file={efpContractFile}
                                    onPosted={(result) => {
                                        if (result.errors.length > 0) {
                                            addMessage('assistant',
                                                `The EFP was posted with problems:\n${result.errors.map(e => `• ${e}`).join('\n')}`);
                                            enqueueSnackbar('The EFP posted with problems', { variant: 'warning' });
                                        } else {
                                            addMessage('assistant',
                                                `Posted the exchange for physical: 5 positions, `
                                                + `${result.allocationCount} allocations`
                                                + `${result.basisReduced ? ', existing basis reduced' : ''}`
                                                + `${result.documentStored ? ', contract stored against the deal ticket' : ''}.`);
                                            enqueueSnackbar('EFP posted', { variant: 'success' });
                                        }
                                        setEfpContractFile(null);
                                        setShowDataReview(false);
                                        onDataRefresh?.();
                                    }}
                                    onCancel={() => {
                                        setEfpContractFile(null);
                                        setShowDataReview(false);
                                        addMessage('assistant', 'Cancelled that contract. Attach another whenever you are ready.');
                                    }}
                                />
                            </Box>
                        )}

                        {showDataReview && wizardData && (
                            <Box sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'auto',
                                backgroundColor: CHAT_COLORS.panel
                            }}>
                                <ImportWizard
                                    fileName={wizardData.fileName}
                                    rawRows={wizardData.rawRows}
                                    sheetNames={wizardData.sheetNames}
                                    ctx={importContext}
                                    savedMappings={savedMappingsForWizard}
                                    onPersistMappings={handlePersistWizardMappings}
                                    onComplete={(result, model) => {
                                        setWizardData(null);
                                        setShowDataReview(false);
                                        const parts = [`Uploaded **${result.successCount}** ${model.plural}.`];
                                        if (result.errorCount > 0) {
                                            parts.push(`**${result.errorCount}** failed.`);
                                        }
                                        if (result.errors.length > 0) {
                                            parts.push(result.errors.slice(0, 3).join('\n'));
                                        }
                                        addMessage('assistant', parts.join(' '));
                                        enqueueSnackbar(
                                            `Uploaded ${result.successCount} ${model.plural}`,
                                            { variant: result.errorCount > 0 ? 'warning' : 'success' }
                                        );
                                        onDataRefresh?.();
                                    }}
                                    onCancel={() => {
                                        setWizardData(null);
                                        setShowDataReview(false);
                                        addMessage('assistant', 'Cancelled that import. Attach another file whenever you are ready.');
                                    }}
                                />
                            </Box>
                        )}

                        {/* Legacy review table - still used by the multi-tab trade
                            workbook parser, which has not been converged onto the
                            generic pipeline yet. */}
                        {showDataReview && !wizardData && !efpContractFile && (
                            <Box sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                                backgroundColor: CHAT_COLORS.panel
                            }}>
                                {/* Data Review Header */}
                                <Box sx={{ 
                                    p: 2, 
                                    borderBottom: 1, 
                                    borderColor: 'divider',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexShrink: 0
                                }}>
                                    <Box>
                                        <Typography variant="h6">Review & Edit Data</Typography>
                                        <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                            <Chip 
                                                icon={<CheckCircleIcon />} 
                                                label={`${validCount} Valid`} 
                                                color="success" 
                                                size="small" 
                                                variant="outlined"
                                            />
                                            {warningCount > 0 && (
                                                <Chip 
                                                    icon={<WarningIcon />} 
                                                    label={`${warningCount} Warnings`} 
                                                    color="warning" 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            )}
                                            {errorCount > 0 && (
                                                <Chip 
                                                    icon={<ErrorIcon />} 
                                                    label={`${errorCount} Errors`} 
                                                    color="error" 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={handleCancelReview}
                                            disabled={isUploading}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleUploadPositions}
                                            disabled={isUploading || validCount === 0}
                                            startIcon={isUploading ? <CircularProgress size={16} /> : undefined}
                                        >
                                            {isUploading ? 'Uploading...' : `Upload ${validCount} Positions`}
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Per-worksheet tabs (only shown when the file produced multiple sheets) */}
                                {sheetNames.length > 1 && (
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', backgroundColor: 'white', flexShrink: 0 }}>
                                        <Tabs
                                            value={selectedSheet || sheetNames[0]}
                                            onChange={(_, val) => setSelectedSheet(val)}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                        >
                                            {sheetNames.map(name => {
                                                const sheetRows = validatedRows.filter(r => (r.sheetName || 'All') === name);
                                                const errs = sheetRows.filter(r => r.errors.length > 0).length;
                                                const warns = sheetRows.filter(r => r.warnings.length > 0 && r.errors.length === 0).length;
                                                return (
                                                    <Tab
                                                        key={name}
                                                        value={name}
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <span>{name}</span>
                                                                <Chip label={sheetRows.length} size="small" />
                                                                {errs > 0 && <Chip label={errs} size="small" color="error" />}
                                                                {warns > 0 && <Chip label={warns} size="small" color="warning" />}
                                                            </Box>
                                                        }
                                                    />
                                                );
                                            })}
                                        </Tabs>
                                    </Box>
                                )}

                                {/* Editable Data Table */}
                                <TableContainer sx={{ flexGrow: 1, overflow: 'auto' }}>
                                    <Table size="small" stickyHeader sx={{ minWidth: 2600, '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 50 }}>Row</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Commodity *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Counterparty *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Location *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 90 }}>Side</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>Type</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>Volume *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 260 }}>Price</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 110 }}>Unit</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 190 }}>Budget Date *</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 190 }}>Contract Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }}>Trade Date</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>Deal Ticket</TableCell>
                                                <TableCell sx={{ fontWeight: 'bold', minWidth: 70 }}>Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Bulk Edit Row */}
                                            <TableRow sx={{
                                                backgroundColor: CHAT_COLORS.highlight,
                                                '& .MuiTableCell-root': {
                                                    borderBottom: 2,
                                                    borderColor: CHAT_COLORS.accent,
                                                    py: 1.5
                                                }
                                            }}>
                                                <TableCell>
                                                    <Typography variant="caption" fontWeight="bold" color="primary.contrastText">
                                                        ALL
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Tooltip title="Apply values to all rows">
                                                        <Typography variant="caption" fontWeight="bold" color="primary.contrastText">
                                                            Bulk Edit
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={customerData?.commodities || []}
                                                        getOptionLabel={(opt) => opt.commodityName || ''}
                                                        value={null}
                                                        onChange={(_, val) => val && handleBulkUpdate('commodity', val)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Set all..." sx={{ minWidth: 160, backgroundColor: 'white' }} />
                                                        )}
                                                        isOptionEqualToValue={(opt, val) => opt.commodityId === val?.commodityId}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={customerData?.counterparties || []}
                                                        getOptionLabel={(opt) => opt.counterpartyName || ''}
                                                        value={null}
                                                        onChange={(_, val) => val && handleBulkUpdate('counterparty', val)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Set all..." sx={{ minWidth: 160, backgroundColor: 'white' }} />
                                                        )}
                                                        isOptionEqualToValue={(opt, val) => opt.counterpartyId === val?.counterpartyId}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={customerData?.locations || []}
                                                        getOptionLabel={(opt) => opt.locationName || ''}
                                                        value={null}
                                                        onChange={(_, val) => val && handleBulkUpdate('location', val)}
                                                        renderInput={(params) => (
                                                            <TextField {...params} placeholder="Set all..." sx={{ minWidth: 160, backgroundColor: 'white' }} />
                                                        )}
                                                        isOptionEqualToValue={(opt, val) => opt.locationId === val?.locationId}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value=""
                                                        onChange={(e) => e.target.value && handleBulkUpdate('side', e.target.value)}
                                                        displayEmpty
                                                        sx={{ minWidth: 80, backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="" disabled><em>Set all</em></MenuItem>
                                                        <MenuItem value={Side.Buy}>Buy</MenuItem>
                                                        <MenuItem value={Side.Sell}>Sell</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value=""
                                                        onChange={(e) => e.target.value && handleBulkUpdate('positionType', e.target.value)}
                                                        displayEmpty
                                                        sx={{ minWidth: 100, backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="" disabled><em>Set all</em></MenuItem>
                                                        <MenuItem value={PositionType.Future}>Future</MenuItem>
                                                        <MenuItem value={PositionType.Physical}>Physical</MenuItem>
                                                        <MenuItem value={PositionType.Option}>Option</MenuItem>
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="primary.contrastText">—</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="primary.contrastText">—</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        value=""
                                                        onChange={(e) => e.target.value && handleBulkUpdate('unit', e.target.value)}
                                                        displayEmpty
                                                        sx={{ minWidth: 80, backgroundColor: 'white' }}
                                                    >
                                                        <MenuItem value="" disabled><em>All</em></MenuItem>
                                                        {Object.values(Unit).map(u => (
                                                            <MenuItem key={u} value={u}>{u}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Select
                                                            size="small"
                                                            value=""
                                                            onChange={(e) => e.target.value && handleBulkUpdate('budgetMonth', e.target.value)}
                                                            displayEmpty
                                                            sx={{ width: 65, backgroundColor: 'white' }}
                                                        >
                                                            <MenuItem value="" disabled><em>M</em></MenuItem>
                                                            {months.map(m => (
                                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        <Select
                                                            size="small"
                                                            value=""
                                                            onChange={(e) => e.target.value && handleBulkUpdate('budgetYear', e.target.value)}
                                                            displayEmpty
                                                            sx={{ width: 80, backgroundColor: 'white' }}
                                                        >
                                                            <MenuItem value="" disabled><em>Y</em></MenuItem>
                                                            {years.map(y => (
                                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        <Select
                                                            size="small"
                                                            value=""
                                                            onChange={(e) => e.target.value && handleBulkUpdate('contractMonth', e.target.value)}
                                                            displayEmpty
                                                            sx={{ width: 65, backgroundColor: 'white' }}
                                                        >
                                                            <MenuItem value="" disabled><em>M</em></MenuItem>
                                                            {months.map(m => (
                                                                <MenuItem key={m} value={m}>{m}</MenuItem>
                                                            ))}
                                                        </Select>
                                                        <Select
                                                            size="small"
                                                            value=""
                                                            onChange={(e) => e.target.value && handleBulkUpdate('contractYear', e.target.value)}
                                                            displayEmpty
                                                            sx={{ width: 80, backgroundColor: 'white' }}
                                                        >
                                                            <MenuItem value="" disabled><em>Y</em></MenuItem>
                                                            {years.map(y => (
                                                                <MenuItem key={y} value={y}>{y}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        size="small"
                                                        type="date"
                                                        onChange={(e) => e.target.value && handleBulkUpdate('tradeDate', e.target.value)}
                                                        sx={{ width: 170, backgroundColor: 'white' }}
                                                        InputLabelProps={{ shrink: true }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="primary.contrastText">—</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="caption" color="primary.contrastText">—</Typography>
                                                </TableCell>
                                            </TableRow>
                                            
                                            {/* Data Rows */}
                                            {pagedRows.map((row) => (
                                                <TableRow 
                                                    key={row.id}
                                                    sx={{ 
                                                        backgroundColor: row.errors.length > 0 
                                                            ? 'rgba(244, 67, 54, 0.08)' 
                                                            : row.warnings.length > 0 
                                                                ? 'rgba(255, 152, 0, 0.08)' 
                                                                : 'white',
                                                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                    }}
                                                >
                                                    <TableCell>{row.rowNumber}</TableCell>
                                                    <TableCell>
                                                        <Tooltip title={[...row.errors, ...row.warnings].map(e => `${e.field}: ${e.message}`).join(', ') || 'Valid'}>
                                                            {row.errors.length > 0 ? (
                                                                <ErrorIcon color="error" fontSize="small" />
                                                            ) : row.warnings.length > 0 ? (
                                                                <WarningIcon color="warning" fontSize="small" />
                                                            ) : (
                                                                <CheckCircleIcon color="success" fontSize="small" />
                                                            )}
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Autocomplete
                                                            size="small"
                                                            options={customerData?.commodities || []}
                                                            getOptionLabel={(opt) => opt.commodityName || ''}
                                                            value={row.mappedData.commodity || null}
                                                            onChange={(_, val) => handleRowUpdate(row.id, 'commodity', val)}
                                                            renderInput={(params) => (
                                                                <TextField 
                                                                    {...params} 
                                                                    error={row.errors.some(e => e.field === 'commodity')}
                                                                    sx={{ minWidth: 160 }}
                                                                />
                                                            )}
                                                            isOptionEqualToValue={(opt, val) => opt.commodityId === val?.commodityId}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Autocomplete
                                                            size="small"
                                                            options={customerData?.counterparties || []}
                                                            getOptionLabel={(opt) => opt.counterpartyName || ''}
                                                            value={row.mappedData.counterparty || null}
                                                            onChange={(_, val) => handleRowUpdate(row.id, 'counterparty', val)}
                                                            renderInput={(params) => (
                                                                <TextField 
                                                                    {...params} 
                                                                    error={row.errors.some(e => e.field === 'counterparty')}
                                                                    sx={{ minWidth: 160 }}
                                                                />
                                                            )}
                                                            isOptionEqualToValue={(opt, val) => opt.counterpartyId === val?.counterpartyId}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Autocomplete
                                                            size="small"
                                                            options={customerData?.locations || []}
                                                            getOptionLabel={(opt) => opt.locationName || ''}
                                                            value={row.mappedData.location || null}
                                                            onChange={(_, val) => handleRowUpdate(row.id, 'location', val)}
                                                            renderInput={(params) => (
                                                                <TextField 
                                                                    {...params} 
                                                                    error={row.errors.some(e => e.field === 'location')}
                                                                    sx={{ minWidth: 160 }}
                                                                />
                                                            )}
                                                            isOptionEqualToValue={(opt, val) => opt.locationId === val?.locationId}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            size="small"
                                                            value={row.data.side}
                                                            onChange={(e) => handleRowUpdate(row.id, 'side', e.target.value)}
                                                            sx={{ minWidth: 80 }}
                                                        >
                                                            <MenuItem value={Side.Buy}>Buy</MenuItem>
                                                            <MenuItem value={Side.Sell}>Sell</MenuItem>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            size="small"
                                                            value={row.data.positionType}
                                                            onChange={(e) => handleRowUpdate(row.id, 'positionType', e.target.value)}
                                                            sx={{ minWidth: 100 }}
                                                        >
                                                            <MenuItem value={PositionType.Future}>Future</MenuItem>
                                                            <MenuItem value={PositionType.Physical}>Physical</MenuItem>
                                                            <MenuItem value={PositionType.Option}>Option</MenuItem>
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={row.data.volume}
                                                            onChange={(e) => handleRowUpdate(row.id, 'volume', e.target.value)}
                                                            error={row.errors.some(e => e.field === 'volume')}
                                                            sx={{ width: 240 }}
                                                            inputProps={{ style: { textAlign: 'right' } }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={row.data.price}
                                                            onChange={(e) => handleRowUpdate(row.id, 'price', e.target.value)}
                                                            sx={{ width: 240 }}
                                                            inputProps={{ style: { textAlign: 'right' }, step: '0.01' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Select
                                                            size="small"
                                                            value={row.data.unit}
                                                            onChange={(e) => handleRowUpdate(row.id, 'unit', e.target.value)}
                                                            sx={{ minWidth: 80 }}
                                                        >
                                                            {Object.values(Unit).map(u => (
                                                                <MenuItem key={u} value={u}>{u}</MenuItem>
                                                            ))}
                                                        </Select>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Select
                                                                size="small"
                                                                value={row.data.budgetDate?.month || 1}
                                                                onChange={(e) => handleRowUpdate(row.id, 'budgetMonth', e.target.value)}
                                                                error={row.errors.some(e => e.field === 'budgetDate')}
                                                                sx={{ width: 65 }}
                                                            >
                                                                {months.map(m => (
                                                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            <Select
                                                                size="small"
                                                                value={row.data.budgetDate?.year || dayjs().year()}
                                                                onChange={(e) => handleRowUpdate(row.id, 'budgetYear', e.target.value)}
                                                                error={row.errors.some(e => e.field === 'budgetDate')}
                                                                sx={{ width: 80 }}
                                                            >
                                                                {years.map(y => (
                                                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Select
                                                                size="small"
                                                                value={row.data.contractDate?.month || 1}
                                                                onChange={(e) => handleRowUpdate(row.id, 'contractMonth', e.target.value)}
                                                                sx={{ width: 65 }}
                                                            >
                                                                {months.map(m => (
                                                                    <MenuItem key={m} value={m}>{m}</MenuItem>
                                                                ))}
                                                            </Select>
                                                            <Select
                                                                size="small"
                                                                value={row.data.contractDate?.year || dayjs().year()}
                                                                onChange={(e) => handleRowUpdate(row.id, 'contractYear', e.target.value)}
                                                                sx={{ width: 80 }}
                                                            >
                                                                {years.map(y => (
                                                                    <MenuItem key={y} value={y}>{y}</MenuItem>
                                                                ))}
                                                            </Select>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            type="date"
                                                            value={row.data.tradeDate || ''}
                                                            onChange={(e) => handleRowUpdate(row.id, 'tradeDate', e.target.value)}
                                                            sx={{ width: 170 }}
                                                            InputLabelProps={{ shrink: true }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextField
                                                            size="small"
                                                            value={row.data.dealTicketNumber || ''}
                                                            onChange={(e) => handleRowUpdate(row.id, 'dealTicketNumber', e.target.value)}
                                                            sx={{ width: 150 }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton 
                                                            size="small" 
                                                            color="error"
                                                            onClick={() => handleDeleteRow(row.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={editableRows.length}
                                    page={page}
                                    onPageChange={(_, newPage) => setPage(newPage)}
                                    rowsPerPage={rowsPerPage}
                                    onRowsPerPageChange={(e) => {
                                        setRowsPerPage(parseInt(e.target.value, 10));
                                        setPage(0);
                                    }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider', backgroundColor: 'white' }}
                                />

                                {/* Already-existing positions: read-only, excluded from upload, collapsed by default. */}
                                {duplicateRows.length > 0 && (
                                    <Box sx={{ flexShrink: 0, borderTop: 2, borderColor: 'warning.main', backgroundColor: 'rgba(255, 152, 0, 0.06)' }}>
                                        <Box
                                            onClick={() => setShowDuplicates(s => !s)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 2,
                                                py: 1,
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': { backgroundColor: 'rgba(255, 152, 0, 0.12)' }
                                            }}
                                        >
                                            <WarningIcon color="warning" fontSize="small" />
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                Already in database ({duplicateRows.length}) — will not be uploaded
                                            </Typography>
                                            <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                                                {showDuplicates ? 'Hide' : 'Show'}
                                            </Typography>
                                        </Box>
                                        {showDuplicates && (
                                            <TableContainer sx={{ maxHeight: 300, overflow: 'auto' }}>
                                                <Table size="small" stickyHeader sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Row</TableCell>
                                                            {sheetNames.length > 1 && <TableCell sx={{ fontWeight: 'bold' }}>Sheet</TableCell>}
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Commodity</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Side</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Volume</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Price</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Contract</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Trade Date</TableCell>
                                                            <TableCell sx={{ fontWeight: 'bold' }}>Deal Ticket</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {duplicateRows.map(row => (
                                                            <TableRow key={row.id}>
                                                                <TableCell>{row.rowNumber}</TableCell>
                                                                {sheetNames.length > 1 && <TableCell>{row.sheetName || ''}</TableCell>}
                                                                <TableCell>{row.mappedData.commodity?.commodityName || ''}</TableCell>
                                                                <TableCell>{row.data.side}</TableCell>
                                                                <TableCell align="right">{row.data.volume}</TableCell>
                                                                <TableCell align="right">{row.data.price}</TableCell>
                                                                <TableCell>{row.data.contractDate?.month}/{row.data.contractDate?.year}</TableCell>
                                                                <TableCell>{row.data.tradeDate || ''}</TableCell>
                                                                <TableCell>{row.data.dealTicketNumber || ''}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                </>
            </ChatShell>

            {/* Column Mapping Dialog */}
            <Dialog 
                open={showMappingDialog} 
                onClose={() => setShowMappingDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HelpOutlineIcon color="warning" />
                        <Typography variant="h6">Map Unmapped Columns</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        The following columns from your file couldn't be automatically mapped. 
                        Please specify where each column's data should go, or choose to ignore it.
                    </Alert>
                    
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Column Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Sample Values</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', width: 250 }}>Map To</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unmappedColumns.map((col) => (
                                <TableRow key={col.originalName}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="medium">
                                            {col.originalName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            {col.sampleValues.slice(0, 3).map((val, i) => (
                                                <Chip 
                                                    key={i} 
                                                    label={val.length > 20 ? val.substring(0, 20) + '...' : val} 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            ))}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={columnMappings[col.originalName] || ''}
                                                onChange={(e) => handleMappingChange(col.originalName, e.target.value || null)}
                                                displayEmpty
                                            >
                                                <MenuItem value="">
                                                    <em>-- Select target field --</em>
                                                </MenuItem>
                                                {AVAILABLE_TARGET_FIELDS.map(field => (
                                                    <MenuItem 
                                                        key={field.value} 
                                                        value={field.value}
                                                        sx={field.value === 'ignore' ? { color: 'text.secondary', fontStyle: 'italic' } : {}}
                                                    >
                                                        {field.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipMappings} color="inherit">
                        Skip (Ignore All)
                    </Button>
                    <Button
                        onClick={handleApplyMappings}
                        startIcon={<LinkIcon />}
                    >
                        Apply Mappings
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Saved Mappings Management Dialog */}
            <Dialog
                open={showSavedMappingsDialog}
                onClose={() => setShowSavedMappingsDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BookmarksIcon sx={{ color: CHAT_COLORS.accent }} />
                        <Typography variant="h6">Saved File Mappings</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        These mappings are remembered from previous uploads and applied automatically
                        when you upload a file with the same name (or the same column shape). Delete
                        an entry to be prompted again the next time that file is uploaded.
                    </Alert>

                    {savedMappings.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">
                                No saved mappings yet. Upload a file with custom columns and apply
                                mappings to save them here.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense>
                            {savedMappings.map(mapping => {
                                const entries = Object.entries(mapping.columnMappings)
                                    .filter(([, target]) => target && target !== '');
                                return (
                                    <ListItem
                                        key={`${mapping.fileName}__${mapping.columnSignature}`}
                                        sx={{
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            mb: 1,
                                            alignItems: 'flex-start',
                                            pr: 8
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {mapping.fileName}
                                                    </Typography>
                                                    <Chip
                                                        label={`${entries.length} mapping${entries.length === 1 ? '' : 's'}`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Chip
                                                        label={`Used ${mapping.useCount}\u00d7`}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Typography variant="caption" color="text.secondary">
                                                        Last used {dayjs(mapping.lastUsed).format('MMM D, YYYY h:mm A')}
                                                    </Typography>
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                    {entries.map(([col, target]) => {
                                                        // The wizard can point several fields at one
                                                        // column (a date column feeding both the
                                                        // month and the year), stored pipe-joined.
                                                        const label = String(target)
                                                            .split(PERSISTED_FIELD_SEPARATOR)
                                                            .map(key => AVAILABLE_TARGET_FIELDS.find(f => f.value === key)?.label || key)
                                                            .join(' + ');
                                                        return (
                                                            <Chip
                                                                key={col}
                                                                size="small"
                                                                label={`${col} \u2192 ${label}`}
                                                                color={target === 'ignore' ? 'default' : 'primary'}
                                                                variant="outlined"
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            }
                                            secondaryTypographyProps={{ component: 'div' }}
                                        />
                                        <ListItemSecondaryAction>
                                            <Tooltip title="Delete saved mapping">
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => handleDeleteSavedMapping(mapping.fileName)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    {savedMappings.length > 0 && (
                        <Button onClick={handleClearAllSavedMappings} color="error">
                            Clear All
                        </Button>
                    )}
                    <Button onClick={() => setShowSavedMappingsDialog(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ChatAssistant;